import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  Grid,
  IconButton,
  MenuItem,
  TextField,
  Typography,
  CircularProgress,
  Container,
  CardHeader,
  Divider,
  CardContent
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import request from '../../utils/request';
import { LoadingButton } from '@mui/lab';
import SnackbarAlert from '../common/SnackbarAlert';

interface Product {
  id: number;
  name: string;
}

interface QCChecklistItem {
  requirement: string;
  checks: string[];
}

const RequiredLabel = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="body2" fontWeight="bold" gutterBottom>
    {children} <span style={{ color: 'red' }}>*</span>
  </Typography>
);

const CreateEditForm = ({ closeCreateEditForm, editData }: any) => {
  const [loader, setLoader] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [productOptions, setProductOptions] = useState<Product[]>([]);
  const [showSnackBarAlert, setShowSnackBarAlert] = useState({
    status: false,
    type: 'success',
    message: null
  });

  const validationSchema = Yup.object({
    policyProductId: Yup.string().required('Product is required'),
    title: Yup.string().trim().required('Title is required'),
    description: Yup.string().trim().required('Description is required'),
    qcChecklist: Yup.array().of(
      Yup.object().shape({
        requirement: Yup.string().trim().required('Requirement is required'),
        checks: Yup.array().of(
          Yup.string().trim().required('Check is required')
        )
      })
    )
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      policyProductId: editData?.policyProductId?.toString() || '',
      title: editData?.title || '',
      description: editData?.description || '',
      qcChecklist:
        editData?.qcCheckList?.map((item: any) => {
          if (typeof item === 'string') {
            try {
              return JSON.parse(item);
            } catch {
              return {
                requirement: '',
                checks: []
              };
            }
          }
          return item;
        }) || []
    },

    validationSchema,

    onSubmit: async (values) => {
      try {
        setLoader(true);

        const payload = {
          policyProductId: Number(values.policyProductId),
          title: values.title.trim(),
          description: values.description.trim(),
          qcCheckList: values.qcChecklist,
          ...(editData?.id && { id: editData.id })
        };

        const res = await request({
          url: '/listOfRequirements',
          method: editData?.id ? 'PUT' : 'POST',
          data: payload
        });

        const responseData = res?.data || res;

        // IMPORTANT FIX
        if (responseData?.status === false) {
          setShowSnackBarAlert({
            status: true,
            type: 'error',
            message: responseData?.message || 'Unable to save data'
          });
          return;
        }

        setShowSnackBarAlert({
          status: true,
          type: 'success',
          message: editData?.id
            ? 'LOR/LOD Updated Successfully'
            : 'LOR/LOD Created Successfully'
        });

        setTimeout(() => {
          closeCreateEditForm();
        }, 1800);
      } catch (error: any) {
        setShowSnackBarAlert({
          status: true,
          type: 'error',
          message:
            error?.response?.data?.message ||
            error?.message ||
            'Internal server error'
        });
      } finally {
        setLoader(false);
      }
    }
  });

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoadingProducts(true);

        const res = await request({
          url: '/policyProduct/getProductListForClaims',
          method: 'POST',
          data: {
            search: null,
            orderColumn: 'id',
            orderDirection: 'DESC'
          }
        });

        const list =
          res?.data?.policyProductData || res?.['policyProductData'] || [];

        const products = [...list];

        if (
          editData?.policyProductId &&
          !products.some(
            (item) => String(item.id) === String(editData.policyProductId)
          )
        ) {
          products.push({
            id: editData.policyProductId,
            name: editData?.policyProduct?.name || 'Selected Product'
          });
        }

        setProductOptions(products);
      } catch {
        setShowSnackBarAlert({
          status: true,
          type: 'error',
          message: 'Failed to load products'
        });
      } finally {
        setLoadingProducts(false);
      }
    };

    fetchProducts();
  }, [editData]);

  const handleSnackbarClose = () => {
    setShowSnackBarAlert({
      status: false,
      type: 'success',
      message: null
    });
  };

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value;

    formik.setFieldValue('title', input);

    const parts = input
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);

    const newChecklist = parts.map((part) => {
      const existing = formik.values.qcChecklist.find(
        (item: QCChecklistItem) => item.requirement === part
      );

      return (
        existing || {
          requirement: part,
          checks: []
        }
      );
    });

    formik.setFieldValue('qcChecklist', newChecklist);
  };

  if (loadingProducts && !editData?.id) {
    return (
      <Box display="flex" justifyContent="center" mt={5}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <SnackbarAlert
        showSnackBarAlert={showSnackBarAlert}
        handleSnackbarClose={handleSnackbarClose}
      />

      <Container maxWidth="lg">
        <Grid container justifyContent="center">
          <Grid item xs={12}>
            <Card sx={{ p: 3 }}>
              <CardHeader
                title={editData?.id ? 'Update LOR/LOD' : 'Create LOR/LOD'}
              />

              <Divider />

              <CardContent>
                <form onSubmit={formik.handleSubmit}>
                  <Grid container spacing={2}>
                    {/* Product */}
                    <Grid item xs={12} md={6}>
                      <RequiredLabel>Product</RequiredLabel>

                      <TextField
                        select
                        fullWidth
                        size="small"
                        name="policyProductId"
                        value={formik.values.policyProductId}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        disabled={loadingProducts}
                        error={
                          formik.touched.policyProductId &&
                          Boolean(formik.errors.policyProductId)
                        }
                        helperText={
                          formik.touched.policyProductId &&
                          formik.errors.policyProductId
                        }
                      >
                        {productOptions.map((item) => (
                          <MenuItem key={item.id} value={String(item.id)}>
                            {item.name}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>

                    {/* Title */}
                    <Grid item xs={12} md={6}>
                      <RequiredLabel>Title</RequiredLabel>

                      <TextField
                        fullWidth
                        size="small"
                        name="title"
                        value={formik.values.title}
                        onChange={
                          editData?.id ? formik.handleChange : handleTitleChange
                        }
                        onBlur={formik.handleBlur}
                        error={
                          formik.touched.title && Boolean(formik.errors.title)
                        }
                        helperText={formik.touched.title && formik.errors.title}
                      />
                    </Grid>

                    {/* QC Checklist */}
                    <Grid item xs={12}>
                      <RequiredLabel>Requirements Checklist</RequiredLabel>

                      {formik.values.qcChecklist.map(
                        (item: QCChecklistItem, index: number) => (
                          <Box key={index} sx={{ mb: 3 }}>
                            <TextField
                              fullWidth
                              size="small"
                              label={`Requirement ${index + 1}`}
                              value={item.requirement}
                              onChange={(e) => {
                                const updated = [...formik.values.qcChecklist];

                                updated[index] = {
                                  ...updated[index],
                                  requirement: e.target.value
                                };

                                formik.setFieldValue('qcChecklist', updated);
                              }}
                              sx={{ mb: 1 }}
                            />

                            {item.checks.map((check, cIndex) => (
                              <Box key={cIndex} display="flex" gap={1} mb={1}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  value={check}
                                  placeholder={`Check ${cIndex + 1}`}
                                  onChange={(e) => {
                                    const updated = [
                                      ...formik.values.qcChecklist
                                    ];

                                    updated[index].checks[cIndex] =
                                      e.target.value;

                                    formik.setFieldValue(
                                      'qcChecklist',
                                      updated
                                    );
                                  }}
                                />

                                <IconButton
                                  color="error"
                                  onClick={() => {
                                    const updated = [
                                      ...formik.values.qcChecklist
                                    ];

                                    updated[index].checks.splice(cIndex, 1);

                                    formik.setFieldValue(
                                      'qcChecklist',
                                      updated
                                    );
                                  }}
                                >
                                  <CloseIcon />
                                </IconButton>
                              </Box>
                            ))}

                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<AddCircleOutlineIcon />}
                              onClick={() => {
                                const updated = [...formik.values.qcChecklist];

                                updated[index].checks.push('');

                                formik.setFieldValue('qcChecklist', updated);
                              }}
                            >
                              Add QC Check
                            </Button>
                          </Box>
                        )
                      )}
                    </Grid>

                    {/* Description */}
                    <Grid item xs={12}>
                      <RequiredLabel>Description</RequiredLabel>

                      <TextField
                        fullWidth
                        multiline
                        rows={4}
                        name="description"
                        value={formik.values.description}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                          formik.touched.description &&
                          Boolean(formik.errors.description)
                        }
                        helperText={
                          formik.touched.description &&
                          formik.errors.description
                        }
                      />
                    </Grid>
                  </Grid>

                  <Grid container justifyContent="center" spacing={2} mt={2}>
                    <LoadingButton
                      type="submit"
                      loading={loader}
                      variant="contained"
                    >
                      Save
                    </LoadingButton>

                    <Button
                      variant="contained"
                      color="error"
                      onClick={closeCreateEditForm}
                    >
                      Cancel
                    </Button>
                  </Grid>
                </form>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </>
  );
};

export default CreateEditForm;
